import { ORIGINAL_VIDEO_ID, SCREENSHARE_VIDEO_ID } from '../constants/screenshare';
import { selectSpeakerAnalysis, selectSpeakerDetails } from '../selectors/contentMetadataStoreSelectors';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { Clip } from '../types/clip-types';
import { SpeakerWithDetails } from '../types/speaker-types';
import { SpeakersAnalysis } from '../types/speakers-analysis';
import { getActiveSpeakersWithinTimeRange } from './activeSpeaker';
import { isEntityScreenshare } from './activityUtils';
import { getAlignedRecordings } from './alignedRecordings';
import {
  getClipBroadcastId,
  getIsDuplicateRecording,
  isClipFromRecordingStudio,
  isClipFromUploads,
  isClipPreRecordedEvent,
  isFullLengthClip,
  isLayoutWithVisionDataEnabledPure,
  isLiveEventRemotionWithPreviewAndAlignedStream
} from './clipUtils';
import { getVideoDetailsFromSpeaker } from './speakerUtils';

export interface VideoRangeType {
  videoId: string;
  ranges: { start: number; end: number }[];
  isScreenshare: boolean;
  isOriginalVideo: boolean;
}

export const getVideosAndTimeRangesFromAlignedStream = ({
  broadcastId,
  contentMetadataStoreInstance
}: {
  broadcastId: string | null;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}): VideoRangeType[] => {
  return getAlignedRecordings({ broadcastId: broadcastId, contentMetadataStoreInstance }).map(recording => {
    const { stage_time_spans, id: videoId, entity } = recording;
    const isScreenshare = isEntityScreenshare(entity);

    const ranges: { start: number; end: number }[] = (stage_time_spans || []).map(times => ({
      start: times.start_time,
      end: times.end_time
    }));

    return {
      videoId,
      ranges,
      isScreenshare,
      isOriginalVideo: false
    };
  });
};
export const getTimeRangesForOriginalVideo = ({ clip }: { clip: Clip }) => {
  return {
    videoId: ORIGINAL_VIDEO_ID,
    ranges: [{ start: clip.asset_metadata.start, end: clip.asset_metadata.end }],
    isScreenshare: false,
    isOriginalVideo: true
  };
};
export const getScreenshareVideoRanges = (
  screenshare_bounds: SpeakersAnalysis['screenshare_bounds']
): VideoRangeType[] => {
  if (!screenshare_bounds || screenshare_bounds.length === 0) {
    return [];
  }

  const screenshareRanges = screenshare_bounds.map(bound => ({
    start: bound.start_time,
    end: bound.end_time
  }));

  return [
    {
      videoId: SCREENSHARE_VIDEO_ID,
      ranges: screenshareRanges,
      isScreenshare: true,
      isOriginalVideo: false
    }
  ];
};
export const getVideosTimeAndRangesForEventsAndUploads = ({
  clip,
  contentMetadataStoreInstance
}: {
  clip: Clip;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}): VideoRangeType[] => {
  const broadcastId = getClipBroadcastId(clip) ?? '';
  const speakersWithDetails = selectSpeakerDetails(broadcastId)(contentMetadataStoreInstance.getState());

  const activeRanges = getActiveSpeakersWithinTimeRange(
    clip.asset_metadata.start,
    clip.asset_metadata.end,
    speakersWithDetails
  );

  const speakerIdToRangeMap: { [key: string]: { start: number; end: number }[] } = {};

  activeRanges.forEach(range => {
    if (!range) {
      return;
    }
    if (!speakerIdToRangeMap[range.speakerId]) {
      speakerIdToRangeMap[range.speakerId] = [];
    }
    speakerIdToRangeMap[range.speakerId].push({ start: range.start, end: range.end });
  });

  return Object.entries(speakersWithDetails).map(([_, speaker]) => {
    const { videoId } = getVideoDetailsFromSpeaker(speaker);

    return {
      videoId,
      ranges: speakerIdToRangeMap[speaker.key] ?? [],
      isScreenshare: false,
      isOriginalVideo: false
    };
  });
};
export const getVideosTimeAndRangesForCustomUploadsClips = ({
  clip,
  speakersAnalysis,
  contentMetadataStoreInstance
}: {
  clip: Clip;
  speakersAnalysis: SpeakersAnalysis;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}): VideoRangeType[] => {
  const broadcastId = getClipBroadcastId(clip) ?? '';
  const speakersWithDetails = selectSpeakerDetails(broadcastId)(contentMetadataStoreInstance.getState());
  const { speaker_mapping, time_analysis, times } = speakersAnalysis;

  const speakerKeyToRangeMap: { [key: string]: { start: number; end: number }[] } = {};

  // Build map of face_id to speaker
  const faceIdToSpeaker: { [faceId: number]: SpeakerWithDetails } = {};
  Object.entries(speaker_mapping).forEach(([speakerId, faceIds]) => {
    const speaker = Object.values(speakersWithDetails).find(s => s.id === speakerId);
    if (speaker) {
      faceIds.forEach(faceId => {
        faceIdToSpeaker[faceId] = speaker;
      });
    }
  });

  // Track ranges for each face_id
  const faceIdRanges: { [faceId: number]: { start: number; end: number } } = {};

  time_analysis.forEach((analysis, index) => {
    const currentTime = times[index];
    const nextTime = index < times.length - 1 ? times[index + 1] : clip.asset_metadata.end;

    analysis.face_positions.forEach(fp => {
      if (!faceIdRanges[fp.face_id]) {
        faceIdRanges[fp.face_id] = { start: currentTime, end: nextTime };
      } else {
        faceIdRanges[fp.face_id].end = nextTime;
      }
    });
  });

  // Convert face ranges to speaker ranges
  Object.entries(faceIdRanges).forEach(([faceId, range]) => {
    const speaker = faceIdToSpeaker[Number(faceId)];
    if (!speaker) {
      return;
    }

    if (!speakerKeyToRangeMap[speaker.key]) {
      speakerKeyToRangeMap[speaker.key] = [];
    }
    speakerKeyToRangeMap[speaker.key].push(range);
  });

  return Object.entries(speakersWithDetails).map(([_, speaker]) => {
    const { videoId } = getVideoDetailsFromSpeaker(speaker);

    return {
      videoId,
      ranges: speakerKeyToRangeMap[speaker.key] ?? [],
      isScreenshare: false,
      isOriginalVideo: false
    };
  });
};
export const getVideosAndTimeRanges = ({
  clip,
  contentMetadataStoreInstance,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled
}: {
  clip: Clip;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
}): VideoRangeType[] => {
  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip)) {
    const videoRanges = getVideosAndTimeRangesFromAlignedStream({
      broadcastId: getClipBroadcastId(clip),
      contentMetadataStoreInstance
    });
    videoRanges.push(getTimeRangesForOriginalVideo({ clip }));
    return videoRanges;
  }

  if (
    (isClipFromUploads(clip) || isClipPreRecordedEvent(clip)) &&
    isFullLengthClip(clip) &&
    !getIsDuplicateRecording(clip)
  ) {
    const speakersAnalysis = selectSpeakerAnalysis(getClipBroadcastId(clip) ?? '')(
      contentMetadataStoreInstance.getState()
    );
    if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
      return [
        ...getScreenshareVideoRanges(speakersAnalysis.screenshare_bounds),
        ...getVideosTimeAndRangesForEventsAndUploads({ clip, contentMetadataStoreInstance }),
        getTimeRangesForOriginalVideo({ clip })
      ];
    }
  }

  if (isLayoutWithVisionDataEnabledPure({ clip, isFullFaceRecEnabled, isFullFaceRecForPreRecordedEnabled })) {
    const speakersAnalysis = selectSpeakerAnalysis(getClipBroadcastId(clip) ?? '')(
      contentMetadataStoreInstance.getState()
    );
    const videos: VideoRangeType[] = [];
    let shouldAddOriginalVideo = false;
    if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
      shouldAddOriginalVideo = true;
      videos.push(...getScreenshareVideoRanges(speakersAnalysis.screenshare_bounds));
    }

    if (speakersAnalysis?.speaker_mapping && Object.keys(speakersAnalysis.speaker_mapping).length > 0) {
      shouldAddOriginalVideo = true;
      videos.push(
        ...getVideosTimeAndRangesForCustomUploadsClips({ clip, contentMetadataStoreInstance, speakersAnalysis })
      );
    } else if (shouldAddOriginalVideo) {
      // If there's screenshare but no speaker mapping, use fallback case
      videos.push(...getVideosTimeAndRangesForEventsAndUploads({ clip, contentMetadataStoreInstance }));
    }

    if (shouldAddOriginalVideo) {
      videos.push(getTimeRangesForOriginalVideo({ clip }));
      return videos;
    }

    return [
      ...getVideosTimeAndRangesForEventsAndUploads({ clip, contentMetadataStoreInstance }),
      getTimeRangesForOriginalVideo({ clip })
    ];
  }

  if (isClipFromRecordingStudio(clip)) {
    return getVideosAndTimeRangesFromAlignedStream({
      broadcastId: getClipBroadcastId(clip),
      contentMetadataStoreInstance
    });
  }

  // Event and upload without facial rec or aligned streams
  return [
    ...getVideosTimeAndRangesForEventsAndUploads({ clip, contentMetadataStoreInstance }),
    getTimeRangesForOriginalVideo({ clip })
  ];
};
